// Shared category configuration for the entire app

// Category order by level (for logical learning progression)
export const CATEGORY_ORDER = {
  beginner: [
    'numbers', 'greetings', 'common expressions', 'body', 'family', 
    'food', 'fruits and veggies', 'weather', 'nature', 'clothes',
    'colors', 'house', 'places', 'transport & directions', 'jobs',
    'school', 'country', 'questions', 'sport', 'Shopping & Bargaining.'
  ],
  intermediate: [
    'numbers', 'greetings', 'common expressions', 'time & days', 'time expressions',
    'family', 'body', 'health', 'food & drinks', 'fruits & veggies',
    'house', 'clothes', 'colors', 'weather', 'animals',
    'emotions', 'like & dislike', 'hobbies', 'education', 'conjunctions',
    'shopping & bargaining', 'ramadan'
  ],
  advanced: [
    'numbers', 'body', 'places', 'house', 'airport',
    'sport', 'shopping & bargaining'
  ]
};

// Comprehensive emoji mapping for all categories
export const categoryIcons = {
  // Numbers & Basics
  'numbers': '🔢',
  'nombres': '🔢',
  
  // Greetings & Expressions
  'greetings': '👋',
  'salutations': '👋',
  'common expressions': '💬',
  'expressions': '💬',
  'quotes': '💬',
  'speaking': '💬',
  
  // Body & Health
  'body': '👤',
  'corps': '👤',
  'health': '❤️',
  'santé': '❤️',
  
  // Family & Relations
  'family': '👨‍👩‍👧‍👦',
  'famille': '👨‍👩‍👧‍👦',
  'relations': '🤝',
  'friends': '🤝',
  'amis': '🤝',
  
  // Food & Drinks
  'food': '🍔',
  'nourriture': '🍔',
  'food & drinks': '🍔',
  'fruits and veggies': '🍎',
  'fruits & veggies': '🍎',
  'fruits': '🍎',
  'légumes': '🍎',
  
  // Weather & Nature
  'weather': '🌦️',
  'météo': '🌦️',
  'nature': '🌿',
  
  // Clothes & Colors
  'clothes': '👕',
  'vêtements': '👕',
  'colors': '🎨',
  'couleurs': '🎨',
  
  // House & Home
  'house': '🏠',
  'maison': '🏠',
  'logement': '🏠',
  
  // Places & Locations
  'places': '📍',
  'lieux': '📍',
  'country': '🌍',
  'pays': '🌍',
  'nationalities': '🌐',
  
  // Transport & Travel
  'transport': '🚗',
  'transport & directions': '🚗',
  'directions': '🧭',
  'travel': '🧳',
  'voyage': '🧳',
  'airport': '✈️',
  'aéroport': '✈️',
  'train station': '🚉',
  'gare': '🚉',
  
  // Work & Education
  'jobs': '💼',
  'travail': '💼',
  'work': '💼',
  'school': '🏫',
  'école': '🏫',
  'education': '🎓',
  'éducation': '🎓',
  
  // Shopping
  'shopping': '🛍️',
  'shopping & bargaining': '🛍️',
  'Shopping & Bargaining.': '🛍️',
  'shop': '🏪',
  'magasin': '🏪',
  
  // Time
  'time': '⏰',
  'temps': '⏰',
  'time & days': '⏰',
  'time expressions': '⏰',
  'night': '🌙',
  'day': '☀️',
  
  // Emotions & Feelings
  'emotions': '💭',
  'feelings': '💭',
  'sentiments': '💭',
  'like & dislike': '👍',
  'likes': '👍',
  'dislikes': '👎',
  
  // Hobbies & Entertainment
  'hobbies': '🎶',
  'loisirs': '🎶',
  'sport': '⚽',
  'sports': '⚽',
  'media': '🎬',
  'entertainment': '🎬',
  
  // Social & Communication
  'communication': '🗣️',
  'social': '📱',
  'café': '☕',
  'restaurant': '🍽️',
  
  // Special Events
  'wedding': '💍',
  'mariage': '💍',
  'ramadan': '🌙',
  'events': '🎉',
  'celebrations': '🎉',
  'fêtes': '🎉',
  
  // Animals
  'animals': '🐾',
  'animaux': '🐾',
  
  // Questions & Grammar
  'questions': '❓',
  'conjunctions': '➕',
  
  // Other
  'tools': '🧰',
  'outils': '🧰',
  'technology': '💡',
  'technologie': '💡',
  'objects': '📦',
  'objets': '📦',
  'cleaning': '🧹',
  'nettoyage': '🧹',
  'safety': '🚨',
  'rules': '🚨',
  'culture': '🎭',
  'jokes': '😂',
  
  // Default
  'default': '📚'
};

// Get emoji for a category - supports custom icons from database
export const getCategoryIcon = (category, customIcon = null) => {
  // If a custom icon is provided from the database, use it
  if (customIcon) {
    return customIcon;
  }
  const lowerCategory = category?.toLowerCase() || '';
  return categoryIcons[lowerCategory] || categoryIcons['default'];
};

// Get sorted categories based on level
export const getSortedCategories = (categories, level) => {
  const levelKey = typeof level === 'number' 
    ? (level === 1 ? 'beginner' : level === 2 ? 'intermediate' : 'advanced')
    : level;
  const order = CATEGORY_ORDER[levelKey] || [];
  
  return [...categories].sort((a, b) => {
    const aLower = a.toLowerCase();
    const bLower = b.toLowerCase();
    const aIndex = order.findIndex(o => o.toLowerCase() === aLower);
    const bIndex = order.findIndex(o => o.toLowerCase() === bLower);
    
    // If both are in the order list, sort by order
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }
    // If only one is in the list, prioritize it
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    // Otherwise, sort alphabetically
    return aLower.localeCompare(bLower);
  });
};

// Get level string from number
export const getLevelString = (levelNumber) => {
  const map = { 1: 'beginner', 2: 'intermediate', 3: 'advanced' };
  return map[levelNumber] || 'beginner';
};
